import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { partnerSelector } from "@dashboard/config/redux";
import {
  fetchCreateDeliveryAsOrder,
  fetchCreateOrderLogistic,
  fetchDeliveryDetails,
  fetchRecreateLogistic,
} from "../fetchers/logistic.fetchers";
import {
  navigateToLogisticPayment,
  prepareLogisticCheckoutFromDetail,
} from "../utils/logistic-payment.utils";
import { deliveryActions } from "../store/delivery.slice";
import {
  deliveryCalculationSelector,
  deliveryConfigDataSelector,
  deliverySelectedProviderSelector,
} from "../store/delivery.selectors";
import { resolveDeliveryCheckoutLabels } from "../utils/delivery-create.utils";
import { useDeliveryToken } from "./use-delivery-token.hook";
import { DELIVERY_ROUTES } from "../delivery.routes";

export const useDeliveryCheckout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams();
  const location = useLocation();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const calculation = useSelector(deliveryCalculationSelector);
  const selectedProvider = useSelector(deliverySelectedProviderSelector);
  const deliveryConfigData = useSelector(deliveryConfigDataSelector);

  const checkoutLabels = useMemo(() => {
    if (!calculation) return { sizeLabel: "", typeLabel: "" };
    return resolveDeliveryCheckoutLabels(
      deliveryConfigData,
      calculation,
      selectedProvider
    );
  }, [calculation, deliveryConfigData, selectedProvider]);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = partner?.userId;
  const isManualCheckout = location.pathname.endsWith(
    `/delivery/${DELIVERY_ROUTES.MANUAL_CHECKOUT}`
  );
  const linkedOrderId =
    !isManualCheckout && orderIdParam ? Number(orderIdParam) : null;

  const handleCreate = useCallback(async () => {
    if (!token || !calculation) {
      toast.error(t("delivery.create.validationError"));
      return;
    }
    setLoading(true);
    setError(null);
    dispatch(deliveryActions.setCheckoutLoading(true));
    try {
      const calc = {
        ...calculation,
        provider:
          selectedProvider?.provider?.id || calculation.provider,
      };

      if (linkedOrderId) {
        await fetchCreateOrderLogistic(
          token,
          linkedOrderId,
          calc as unknown as Record<string, unknown>
        );
        const detail = await fetchDeliveryDetails(token, linkedOrderId);
        dispatch(deliveryActions.setCurrentDetail(detail));
        setCreatedOrderId(linkedOrderId);
        navigateToLogisticPayment(
          dispatch,
          navigate,
          userId!,
          prepareLogisticCheckoutFromDetail(detail, linkedOrderId)
        );
      } else {
        const order = await fetchCreateDeliveryAsOrder(token, calc);
        const detail = await fetchDeliveryDetails(token, order.id);
        dispatch(deliveryActions.setCurrentDetail(detail));
        setCreatedOrderId(order.id);
        navigateToLogisticPayment(
          dispatch,
          navigate,
          userId!,
          prepareLogisticCheckoutFromDetail(detail, order.id)
        );
      }
    } catch {
      setError(t("delivery.checkout.error"));
      dispatch(deliveryActions.setCheckoutError(t("delivery.checkout.error")));
    } finally {
      setLoading(false);
      dispatch(deliveryActions.setCheckoutLoading(false));
    }
  }, [
    token,
    calculation,
    selectedProvider,
    linkedOrderId,
    dispatch,
    navigate,
    userId,
    t,
  ]);

  const handleRecreate = useCallback(async () => {
    const id = createdOrderId || linkedOrderId;
    if (!token || !id) return;
    setLoading(true);
    try {
      await fetchRecreateLogistic(token, id);
      toast.success(t("delivery.checkout.success"));
      setError(null);
    } catch {
      setError(t("delivery.checkout.error"));
    } finally {
      setLoading(false);
    }
  }, [token, createdOrderId, linkedOrderId, t]);

  useEffect(() => {
    if (!calculation && !isManualCheckout && linkedOrderId) {
      navigate(`/${userId}/delivery/create/${linkedOrderId}`);
    }
  }, [calculation, isManualCheckout, linkedOrderId, navigate, userId]);

  const breadcrumbs = [
    {
      label: t("delivery.breadcrumb.delivery"),
      link: `/${userId}/delivery`,
    },
    { label: t("delivery.breadcrumb.checkout") },
  ];

  return {
    calculation,
    selectedProvider,
    sizeLabel: checkoutLabels.sizeLabel,
    typeLabel: checkoutLabels.typeLabel,
    loading,
    error,
    handleCreate,
    handleRecreate,
    breadcrumbs,
    linkedOrderId,
    partner,
  };
};
